import { MultiSymbolMarginContext } from "@shared/features/bots/BotForm/MultiSymbolMarginSelector/MultiSymbolMargin.context";
import { difference, pickBy, union } from "lodash";
import { useContext, useMemo } from "react";

const useSelectAll = () => {
  const {
    selectedSymbols,
    resetData,
    filteredSymbols,
    searchFilteredSymbols,
    setSelectedSymbolsAndPendingValues,
    multiSymbolMarginData,
    botFormDataRef,
    isViewOnly,
    globalSettingsExpanded,
  } = useContext(MultiSymbolMarginContext)!;
  const { setMultiSymbolMarginData } = botFormDataRef.current || {};

  const isAllSelected = useMemo(
    () =>
      !!searchFilteredSymbols.length &&
      searchFilteredSymbols.every((symbol) => selectedSymbols.has(symbol)),
    [searchFilteredSymbols, selectedSymbols]
  );
  const isFiltered = searchFilteredSymbols.length < filteredSymbols.length;

  const selectAll = () => {
    const updatedSymbols = union([...selectedSymbols], searchFilteredSymbols);
    setSelectedSymbolsAndPendingValues(new Set(updatedSymbols));
  };

  const unselectAll = () => {
    const updatedSymbols = difference(
      [...selectedSymbols],
      searchFilteredSymbols
    );
    resetData(undefined, undefined, new Set(updatedSymbols));
  };

  const restoreToGlobal = () => {
    const updatedMarginData = pickBy(
      multiSymbolMarginData,
      (_, symbol) => !selectedSymbols.has(symbol)
    );
    setMultiSymbolMarginData?.(updatedMarginData);
    resetData(false, true, new Set());
  };

  const selectAllData = {
    text: `${isAllSelected ? "Unselect" : "Select"} all${
      isFiltered ? " matching" : ""
    }`,
    onClick: isAllSelected ? unselectAll : selectAll,
    isSelected: false,
    numSelected: selectedSymbols.size,
    unselectAll: unselectAll,
    selectAll,
    shouldDisplay: !!filteredSymbols.length && !isViewOnly,
  };

  const restoreToGlobalData = {
    onClick: restoreToGlobal,
    text: "Reset to Global",
    shouldDisplay: !!selectedSymbols.size,
  };

  const showNumSelectedText = !!selectAllData.numSelected && !isViewOnly;
  const hideSelectAll =
    isViewOnly ||
    (!showNumSelectedText &&
      !selectAllData.shouldDisplay &&
      !filteredSymbols.length);
  const isExpanded =
    !!selectAllData.numSelected && !isViewOnly && !globalSettingsExpanded;

  return {
    selectAllData,
    restoreToGlobalData,
    showNumSelectedText,
    hideSelectAll,
    isExpanded,
  };
};

export default useSelectAll;
